import React, { useRef } from 'react';
import { Tooltip, tooltipClasses, Box, Typography, TypographyProps } from '@mui/material';

export interface HeaderTooltipProps {
  title: string | React.ReactElement;
  popperTextVariant?: TypographyProps['variant'];
  textVariant?: TypographyProps['variant'];
  dataTestId?: string;
}

const MAX_POPPER_HEIGHT = 300;
const MAX_POPPER_WIDTH = 700;
const ENTER_DELAY = 600;
const LEAVE_DELAY = 400;

export const HeaderTooltip: React.FC<HeaderTooltipProps> = ({
  title,
  dataTestId,
  textVariant,
  popperTextVariant,
}) => {
  const tooltipRef = useRef(null);

  const isClamped = (ref: React.RefObject<HTMLElement>) => {
    if (!ref.current) return false;
    const element = ref.current;
    return element?.clientHeight < element?.scrollHeight;
  };

  return (
    <Tooltip
      ref={tooltipRef}
      enterDelay={ENTER_DELAY}
      leaveDelay={LEAVE_DELAY}
      placement='bottom-start'
      disableHoverListener={!isClamped(tooltipRef)}
      componentsProps={{
        popper: {
          sx: {
            [`& .${tooltipClasses.tooltip}`]: {
              backgroundColor: (theme) => theme.palette.common.white,
              color: (theme) => theme.palette.text.primary,
              boxShadow: (theme) => theme.shadows[10],
              maxWidth: MAX_POPPER_WIDTH,
              py: 2,
              px: 0,
            },
          },
        },
      }}
      title={
        <Box overflow='auto' maxHeight={MAX_POPPER_HEIGHT} px={2}>
          <Typography variant={popperTextVariant}>{title}</Typography>
        </Box>
      }
    >
      <Typography
        color='primary.contrastText'
        variant={textVariant}
        datatest-id={dataTestId}
        sx={{
          overflow: 'hidden',
          display: '-webkit-box',
          wordBreak: 'break-word',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
        }}
      >
        {title}
      </Typography>
    </Tooltip>
  );
};
